import { Routes, Route } from 'react-router-dom';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import {
  AccountDetails,
  AccountSummary,
  ActivityLog,
  ActivityLogs,
  AllDownlineListing,
  BannerManagement,
  BetControl,
  BetList,
  BetSetting,
  BussinessReports,
  CasinoBetList,
  CasinoControl,
  CasinoDetail,
  CasinoPlByDownline,
  CasinoPlByPlayer,
  CategoryControl,
  ContentManagement,
  DashboardPage,
  DWRequest,
  EventManagement,
  Export,
  FilteredGames,
  FirebaseNotifications,
  ManualBonusConfig,
  ManualBonusReport,
  GameControl,
  GeneralSettings,
  InactiveMemerList,
  Login,
  ManageAccount,
  NotFoundPage,
  NotificationManagement,
  PlByEvent,
  PlByPlayer,
  PlDownline,
  ProviderControl,
  Risk,
  Setting,
  SubUsers,
  SupportContactManagement,
  TransactionCode,
  TransactionHistory,
  UserAccountStatement,
  UserBetHistory,
  UserBettingPL,
  RoleWiseAccountStatement,
  CryptoConversion,
  BonusStatement,
  PolicyManagement,
  RiskDetailsPage,
  SportAnalysis,
  SportAnalysisEventDetails,
  SportAnalysisRaceDetails,
  Settlement,
  MatchOddsCommissionSetting,
  MatchOddsCommissionReport,
  CoinManagement,
  RolesAndPrivileges,
  Roles,
  AccessControl,
  CreateRoles,
} from '@/components/routes';
import { useRoleCheck } from './hooks/useRoleCheck';
import ProtectedAdminRoutes from './components/routes/ProtectedAdminRoutes';
import ModuleRoute from './components/ModuleRoute';
import { PermissionProvider } from './context/PermissionContext';
import { Permission } from './types/rolesTypes';
import { useGetUserRolesDetailsQuery } from './store/api/rolesApi';
import { skipToken } from '@reduxjs/toolkit/query';

export function App() {
  const user = useSelector((state: RootState) => state.user);
  const userId = user?.data?.id;
  const { data, isLoading, isSuccess } = useGetUserRolesDetailsQuery(
    userId ? Number(userId) : skipToken,
    {
      skip: !userId,
    }
  );
  const userData = data?.data?.[0];

  const { isOwner, isRoleUser, isDownlineUser, isUserFetched, role } = useRoleCheck();

  const ownerOnly = (element: React.ReactNode) => (
    <ProtectedAdminRoutes allowed={isOwner || isRoleUser} isLoading={!isUserFetched || !role}>
      {element}
    </ProtectedAdminRoutes>
  );

  const ownerOrAgent = (element: React.ReactNode) => (
    <ProtectedAdminRoutes
      allowed={isOwner || isRoleUser || isDownlineUser}
      isLoading={!isUserFetched || !role}
    >
      {element}
    </ProtectedAdminRoutes>
  );

  return (
    <PermissionProvider user={userData as Permission} isLoading={isLoading}>
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
        </Route>

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            <Route
              path="/"
              element={
                <ModuleRoute moduleName="Dashboard">
                  <DashboardPage />
                </ModuleRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ModuleRoute moduleName="Dashboard">
                  <DashboardPage />
                </ModuleRoute>
              }
            />
            {/* <Route path="/profile" element={<ProfilePage />} /> */}
            {/* Downline Routes */}
            <Route element={<ModuleRoute moduleName="Users" />}>
              <Route path="/members/:id" element={<AllDownlineListing />} />
              <Route path="/members/:id/:role/:user/:memberid/:upline" element={<SubUsers />} />
              <Route path="/settlement" element={<Settlement />} />
            </Route>
            <Route
              path="/members/:id/sub/:memberid/:userType/:uplineId"
              element={
                <ModuleRoute moduleName="Users" requiredAccess={['read']}>
                  <AccountDetails />
                </ModuleRoute>
              }
            >
              <Route index element={<AccountSummary />} />
              <Route
                path="account-summary"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'update']}>
                    <AccountSummary />
                  </ModuleRoute>
                }
              />

              <Route
                path="account-statement"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <UserAccountStatement />
                  </ModuleRoute>
                }
              />
              <Route
                path="bet-history"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <UserBetHistory />
                  </ModuleRoute>
                }
              />
              <Route
                path="betting-pl"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <UserBettingPL />
                  </ModuleRoute>
                }
              />

              <Route
                path="casino-detail"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <CasinoDetail />
                  </ModuleRoute>
                }
              />

              <Route
                path="transaction-history"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <TransactionHistory />
                  </ModuleRoute>
                }
              />
              <Route
                path="login-history"
                element={
                  <ModuleRoute moduleName="Users" requiredAccess={['read', 'detailedView']}>
                    <ActivityLog />
                  </ModuleRoute>
                }
              />
            </Route>
            {/* Match Odds Commission */}
            <Route element={<ModuleRoute moduleName="Commission" />}>
              <Route
                path="/match-odds-commission-setting"
                element={ownerOnly(<MatchOddsCommissionSetting />)}
              />
              <Route
                path="/match-odds-commission-report"
                element={ownerOnly(<MatchOddsCommissionReport />)}
              />
            </Route>
            {/* Coin Management */}
            <Route element={<ModuleRoute moduleName="CoinManagement" />}>
              <Route path="/coin-management" element={<CoinManagement />} />
            </Route>
            {/* Change Password Routes */}
            <Route path="/settings" element={<Setting />} />
            {/* General Settings */}
            <Route
              path="/general-settings"
              element={
                <ModuleRoute moduleName="General Settings">
                  {ownerOnly(<GeneralSettings />)}
                </ModuleRoute>
              }
            />
            {/* Firebase Notifications */}
            <Route
              path="/firebase-notifications"
              element={
                <ModuleRoute moduleName="FirebaseNotifications">
                  {ownerOnly(<FirebaseNotifications />)}
                </ModuleRoute>
              }
            />
            {/* Event Management*/}
            <Route element={<ModuleRoute moduleName="EventManagement" />}>
              <Route path="/event-management/:status?" element={ownerOnly(<EventManagement />)} />
              <Route path="/bet-setting" element={ownerOnly(<BetSetting />)} />
            </Route>
            {/* Casino management */}
            <Route element={<ModuleRoute moduleName="CasinoControl" />}>
              <Route path="/casino-management" element={ownerOnly(<CasinoControl />)}>
                <Route index element={<CategoryControl />} />
                <Route path="provider" element={<ProviderControl />} />
                <Route path="game" element={<GameControl />} />
              </Route>
              <Route path="/casino-management/games" element={ownerOnly(<FilteredGames />)} />
            </Route>
            {/* Content Management */}
            <Route element={<ModuleRoute moduleName="ContentManagement" />}>
              <Route path="/content-management" element={<ContentManagement />}>
                <Route index element={ownerOnly(<BannerManagement />)} />
                <Route
                  path="notification-management"
                  element={ownerOnly(<NotificationManagement />)}
                />
                <Route path="support-management" element={<SupportContactManagement />} />
              </Route>
            </Route>
            {/* Manual Bonus */}
            <Route path="/manual-bonus">
              <Route path="config" element={ownerOrAgent(<ManualBonusConfig />)} />
              <Route path="report" element={ownerOrAgent(<ManualBonusReport />)} />
            </Route>
            {/* Bonus Management */}
            <Route element={<ModuleRoute moduleName="Bonus" />}>
              <Route path="/bonus">
                <Route path="policy-management" element={ownerOnly(<PolicyManagement />)} />
                <Route path="statement" element={ownerOnly(<BonusStatement />)} />
              </Route>
            </Route>
            <Route element={<ModuleRoute moduleName="BankingManagement" />}>
              <Route path="/requests/:mode?/:type?" element={<DWRequest />} />
              <Route path="/manage-account/:type?" element={<ManageAccount />} />
              <Route path="/crypto-conversion" element={<CryptoConversion />} />
            </Route>
            <Route
              path="/rolewise-account-statement"
              element={
                <ModuleRoute moduleName="Account Statement">
                  <RoleWiseAccountStatement />
                </ModuleRoute>
              }
            />
            {/* Risk Management Routes */}
            <Route element={<ModuleRoute moduleName="RiskManagement" />}>
              <Route path="/risk" element={<Risk />} />
              <Route path="/risk-details" element={<RiskDetailsPage />} />
            </Route>

            {/* Sport Analysis Routes */}
            <Route element={<ModuleRoute moduleName="RiskManagement" />}>
              <Route path="/sport-analysis" element={<SportAnalysis />} />
              <Route
                path="/sport-analysis/event/:eventId"
                element={<SportAnalysisEventDetails />}
              />
              <Route
                path="/sport-analysis/race/:sport/:eventId/:externalId?"
                element={<SportAnalysisRaceDetails />}
              />
            </Route>

            {/* Bet Control Routes */}
            <Route
              path="/bet-control/:section?"
              element={
                <ModuleRoute moduleName="BetControl">
                  <BetControl />
                </ModuleRoute>
              }
            />

            {/* Reports Routes */}
            <Route element={<ModuleRoute moduleName="MyReport" />}>
              <Route path="/bet-list" element={<BetList />} />
              <Route path="/casino-bet-list" element={<CasinoBetList />} />
              <Route path="/pl-by-downline" element={<PlDownline />} />
              <Route path="/pl-by-player" element={<PlByPlayer />} />
              <Route path="/pl-by-event" element={<PlByEvent />} />
              <Route path="/casinopl-by-player" element={<CasinoPlByPlayer />} />
              <Route path="/casinopl-by-downline" element={<CasinoPlByDownline />} />
              {/* activity logs */}
              <Route path="/business-reports" element={<BussinessReports />} />
            </Route>
            <Route
              path="/activity-logs"
              element={
                <ModuleRoute moduleName="Activity Logs">
                  <ActivityLogs />
                </ModuleRoute>
              }
            />
            {/* Export History */}
            <Route
              path="/export"
              element={
                <ModuleRoute moduleName="Export History">
                  <Export />
                </ModuleRoute>
              }
            />
            {/* Roles And Privileges */}
            <Route element={<ModuleRoute moduleName="Roles Privileges" />}>
              <Route path="/roles-and-privileges" element={<RolesAndPrivileges />}>
                <Route index element={<Roles />} />
                <Route path="access" element={<AccessControl />} />
              </Route>
              <Route
                path="create-roles/:id?/:type?"
                element={
                  <ModuleRoute moduleName="Roles Privileges" requiredAccess={['read', 'create']}>
                    <CreateRoles />
                  </ModuleRoute>
                }
              />
            </Route>
          </Route>
        </Route>
        {/* </Route> */}
        <Route path="/transaction-code" element={<TransactionCode />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </PermissionProvider>
  );
}
